import { useEffect, useState } from 'react';
import type { TSelectBase } from 'types/general.types';
import {
  EQuestionType,
  type TAnyClientQuestionType,
  type TAvailableLanguageCodes,
  type TSingleSelectQuestion,
  type TMultipleSelectQuestion,
} from '@hubertai/screening-question-types';
import { useQuestion } from './useQuestion';

async function prepareQuestionAnswersV4(
  questionConfig: TSingleSelectQuestion<string> | TMultipleSelectQuestion<string>,
  applyLanguages?: TAvailableLanguageCodes[], // if empty array ==> show all languages
): Promise<TSelectBase[]> {
  if (!questionConfig) {
    return [];
  }

  const languageList: TAvailableLanguageCodes[] =
    applyLanguages && applyLanguages.length ? applyLanguages : questionConfig.available_languages;

  // Add options in all languages
  const availableOptions: TSelectBase[] = [];

  questionConfig.options.map((option) => {
    const answerLabel = `${languageList.map((l) => option.label[l]).join(' / ')}`;
    availableOptions.push({ label: answerLabel, value: option.id });
  });
  return availableOptions;
}

export function useQuestionAnswerValuesV4(
  nodeId: string | null,
  applyLanguages: TAvailableLanguageCodes[] = [], // if empty array ==> show all languages
): {
  isLoading: boolean;
  list: TSelectBase[];
} {
  const { question, questionConfig, language, version, isLoading: isQuestionLoading } = useQuestion(nodeId);
  const [list, setList] = useState<TSelectBase[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (isQuestionLoading || !nodeId || !language || !question || !questionConfig) {
      setIsLoading(false);
      return;
    }

    async function getAnswers(questionConfig: TAnyClientQuestionType) {
      if (questionConfig.type !== EQuestionType.SingleSelect && questionConfig.type !== EQuestionType.MultipleSelect) {
        setIsLoading(false);
        return;
      }

      if (version === 4) {
        setList(await prepareQuestionAnswersV4(questionConfig, applyLanguages));
      }

      setIsLoading(false);
    }

    getAnswers(questionConfig);
  }, [nodeId, language, isQuestionLoading]);

  return {
    isLoading,
    list,
  };
}
